import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  MenuItem,
  Grid,
  ListItemText,
  IconButton,
  Paper,
  TextField,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import { withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../SearchableSelect';
import ProviderSearchBar from '../ProviderSearchBar';
import Button from '../Button';
import { fetchProviders } from '../../services/api';
import * as adminApi from '../../services/api/admin';

const emptyFormInfo = {
  visitType: '',
  durationMinutes: '',
  priorityLevel: '',
  uuid: '',
  provider: {},
  tenancy: {},
  practice: {},
}

function AppointmentDurationRules(props) {
  const {
    classes: {
      form,
      paper,
      fieldsBlock,
    },
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [allFormInfo, setAllFormInfo] = useState([emptyFormInfo]);
  const [validationErrors, setValidationErrors] = useState([{}])
  const priorityLevelList = [
    { value: 'practiceVisitType', key: 1, label: 'practice' },
    { value: 'providerVisitType', key: 2, label: 'provider' },
    { value: 'tenancyVisitType', key: 3, label: 'tenancy' },
  ];

  /* when user types in search bar for a provider/tenancy/practice this fires,
     hits server for providers matching name. what it returns become the options
     in the dropdown of the search bar */
  const getDropOptions = (hierarchy) => async (searchTerm) => {
    let hierarchyOptions = [];
    if (hierarchy === 'provider') {
      hierarchyOptions = await fetchProviders({ providerName: searchTerm, });
    } else if (hierarchy === 'tenancy') {
      hierarchyOptions = await adminApi.fetchTenanciesByLocation(searchTerm);
    } else if (hierarchy === 'practice') {
      hierarchyOptions = await adminApi.fetchPracticesByName(searchTerm);
    }

    const optionList = hierarchyOptions.map(option => ({
      value: option.id,
      label: option.name,
      id: option.id,
      type: hierarchy
    }))
    return optionList
  };

  /* this fires when ever a form field being changed(for input fields) or selected (dropdowns...)
   the input information is passed and updates the forms data array at the propriate index.  */
  const onFieldChange = (i) => async e => {
    if (!e) return
    const nextFormInfos = []
    const nextValidationErrors = []
    allFormInfo.forEach((formInfo, index) => {
      if (i === index) {
        if (e.hasOwnProperty('target') && (['visitType', 'durationMinutes', 'priorityLevel'].includes(e.target.name))) {

          nextFormInfos.push({ ...formInfo, [e.target.name]: e.target.value });
          nextValidationErrors.push({ ...validationErrors[index], [e.target.name]: false })

        } else {

          nextFormInfos.push({ ...formInfo, uuid: e.id, [e.type || 'provider']: e });
          nextValidationErrors.push({ ...validationErrors[index], selection: false })

        }
      } else {
        nextFormInfos.push({ ...formInfo })
        nextValidationErrors.push({ ...validationErrors[index] })
      }
    });
    setValidationErrors(nextValidationErrors);
    setAllFormInfo(nextFormInfos)
  };

  const addOneForm = () => {
    const nextFormInfo = [...allFormInfo];
    nextFormInfo.push(Object.assign({}, emptyFormInfo));
    setAllFormInfo(nextFormInfo);

    const nextValidationErrors = [...validationErrors, {}];
    setValidationErrors(nextValidationErrors);
  };

  const delLastForm = () => {
    const nextFormInfo = [...allFormInfo];
    nextFormInfo.pop();
    setAllFormInfo(nextFormInfo);

    const nextValidationErrors = [...validationErrors];
    nextValidationErrors.pop();
    setValidationErrors(nextValidationErrors);
  }

  const validate = () => {
    let valid = true
    const nextValidationErrors = []
    for (let i = 0; i < allFormInfo.length; i++) {
      nextValidationErrors.push({})
      if (!allFormInfo[i].visitType) {
        valid = false
        nextValidationErrors[i].visitType = 'Required'
      }
      if (!allFormInfo[i].durationMinutes) {
        valid = false
        nextValidationErrors[i].durationMinutes = 'Required'
      }
      if (!allFormInfo[i].priorityLevel) {
        valid = false
        nextValidationErrors[i].priorityLevel = 'Required'
      }
      if (!allFormInfo[i].provider.id && !allFormInfo[i].practice.id && !allFormInfo[i].tenancy.id) {
        valid = false
        nextValidationErrors[i].selection = 'Required'
      }
    }
    setValidationErrors(nextValidationErrors)
    return valid
  }

  //this gets executed when form is submited
  const makeAptDurationRule = async () => {
    try {
      await adminApi.createAptDurationRules(allFormInfo);
      setAllFormInfo([emptyFormInfo])
      enqueueSnackbar('Successfully created appointment duration!');
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Unable to create appointment duration. Please try again.');
    }
  };

  const genOneForm = (i) => {
    const onFieldChangeRef = onFieldChange(i);
    return (
      <Grid container item direction="column" key={`form-${i}`}>
        <Paper elevation={1} className={paper} >
          <Grid container item xs={12} spacing={24} key={`for-row-${i}`} className={fieldsBlock}>
            <Grid item xs={3} >
              <TextField
                variant="outlined"
                label="Visit Type"
                helperText="What kind of appointment are you trying to save?"
                name="visitType"
                value={allFormInfo[i].visitType}
                onChange={onFieldChangeRef}
                error={validationErrors[i].visitType}
                helperText={validationErrors[i].visitType}
                className={form}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                variant="outlined"
                label="Duration in Minutes"
                helperText="Integers only."
                name="durationMinutes"
                value={allFormInfo[i].durationMinutes}
                onChange={onFieldChangeRef}
                error={validationErrors[i].durationMinutes}
                helperText={validationErrors[i].durationMinutes}
                className={form}
              />
            </Grid>
            <Grid item xs={2}>
              <TextField
                variant="outlined"
                label="Rule Hierachy"
                name="priorityLevel"
                default={'provider'}
                value={allFormInfo[i].priorityLevel}
                error={validationErrors[i].priorityLevel}
                helperText={validationErrors[i].priorityLevel}
                onChange={onFieldChangeRef}
                className={form}
                select
              >
                {priorityLevelList.map(type => (
                  <MenuItem key={type.key} value={type.value}>
                    <ListItemText primary={type.label} />
                  </MenuItem>
                ))}
              </TextField>

            </Grid>

            <Grid item xs={4}>
              {allFormInfo[i].priorityLevel === 'providerVisitType' && (
                <ProviderSearchBar
                  label="Provider"
                  name="provider"
                  placeholder=""
                  onSelect={onFieldChangeRef}
                  errorMessage={validationErrors[i].selection}
                  className={form}
                />
              )}
              {allFormInfo[i].priorityLevel === 'tenancyVisitType' && (
                <SearchableSelect
                  searchable
                  label="Tenancy"
                  name="tenancy"
                  placeholder=""
                  fetchAsyncOptions={getDropOptions('tenancy')}
                  value={allFormInfo[i].tenancy}
                  options={[]}
                  onChange={onFieldChangeRef}
                  error={validationErrors[i].selection}
                  errorMessages={[validationErrors[i].selection]}
                  className={form}
                />
              )}
              {allFormInfo[i].priorityLevel === 'practiceVisitType' && (
                <SearchableSelect
                  searchable
                  label="Practice"
                  name="practice"
                  placeholder=""
                  fetchAsyncOptions={getDropOptions('practice')}
                  value={allFormInfo[i].practice}
                  options={[]}
                  onChange={onFieldChangeRef}
                  error={validationErrors[i].selection}
                  errorMessages={[validationErrors[i].selection]}
                  className={form}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid container direction="column" spacing={24}>
      <Grid container direction="column" spacing={16}>
        {allFormInfo.map((_, i) => genOneForm(i))}
      </Grid>
      <Grid container item xs={12} justify={'center'}>
        <Button
          color="primary"
          variant="contained"
          onClick={makeAptDurationRule}
          onClickValidation={validate}
          disableDuringOnClick
        >
          Save
        </Button>
        <IconButton onClick={addOneForm}>
          <Add />
        </IconButton>

        <IconButton onClick={delLastForm}>
          <Remove />
        </IconButton>
      </Grid>
    </Grid>
  );
}

AppointmentDurationRules.propTypes = {
  classes: PropTypes.object,
};

const styles = () => ({
  form: {
    padding: '0.5em 0',
    width: '100%'
  },
  paper: {
    padding: '10px',
  },
  fieldsBlock: {
    marginLeft: '50px'
  }
});

export default withStyles(styles)(AppointmentDurationRules);
